import {
  ReconciliationInvoice,
  ReconciliationSupplierDetail,
  useRegenerateInvoiceApi,
  useUpdateInvoicePaymentApi,
} from 'apis/reconciliation';
import dayjs from 'dayjs';
import {
  Badge,
  HStack,
  Icon,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  useCustomToast,
} from 'design-system';
import { InvoiceStatusChip } from 'pages/reconciliation/components/invoice-status-chip';
import { MarkPaidPopover } from 'pages/reconciliation/components/mark-paid-popover';
import { formatAed } from 'pages/reconciliation/utils/format';
import { FiMoreVertical } from 'react-icons/fi';
import { useQueryClient } from 'react-query';

export interface IInvoicesList {
  month: string;
  detail: ReconciliationSupplierDetail;
}

/** Compact ledger of the supplier-month's proforma invoices (newest first): number (download),
 * scope badge, amount, paid chip, date, and an overflow with Regenerate PDF — a correction tool,
 * deliberately never a primary action. Payment toggles stay allowed on closed months. */
export const InvoicesList = ({ month, detail }: IInvoicesList) => {
  const toast = useCustomToast();
  const queryClient = useQueryClient();
  const invoices = detail.invoices ?? [];
  const isClosed = detail.status === 'closed';

  const invalidate = () => {
    queryClient.invalidateQueries(['reconciliation-supplier-details', month, detail.supplierId]);
    queryClient.invalidateQueries(['reconciliation-supplier-orders', month, detail.supplierId]);
    queryClient.invalidateQueries(['reconciliation-suppliers']);
    queryClient.invalidateQueries(['reconciliation-summary', month]);
  };

  const { mutate: regenerate } = useRegenerateInvoiceApi({
    onSuccess: ({ invoice }) => {
      toast({ description: `${invoice.invoiceNumber} re-rendered — same number, fresh PDF`, status: 'success' });
      invalidate();
    },
  });

  const { mutate: updatePayment } = useUpdateInvoicePaymentApi({
    onSuccess: () => {
      toast({ description: 'Marked as unpaid — the month’s collected amount has been updated', status: 'success' });
      invalidate();
    },
  });

  if (!invoices.length) return null;

  const regenerateDisabledReason = (inv: ReconciliationInvoice): string | null => {
    if (inv.isUploaded) return 'Uploaded manually — regenerating would replace the original PDF';
    if (inv.isPaid) return 'Cannot regenerate a paid invoice';
    if (isClosed) return 'Month is closed';
    return null;
  };

  return (
    <Stack spacing='xs' pt='sm' borderTop='1px solid' borderColor='gray.100'>
      <Text fontSize='2xs' fontWeight='bold' color='gray.500' textTransform='uppercase' letterSpacing='wide'>
        Invoices
      </Text>
      {invoices.map((inv) => (
        <HStack key={inv.id} justify='space-between' spacing='sm'>
          <HStack spacing='sm' minW='0'>
            <Link
              href={inv.url}
              target='_blank'
              rel='noopener noreferrer'
              color='red.500'
              fontSize='sm'
              fontWeight='medium'
              whiteSpace='nowrap'>
              {inv.invoiceNumber}
            </Link>
            <Badge fontSize='2xs' textTransform='none' colorScheme={inv.scope === 'monthly' ? 'purple' : 'gray'}>
              {inv.scope === 'monthly' ? 'Monthly' : inv.orderCode ?? 'Order'}
            </Badge>
            <InvoiceStatusChip commission={0} invoice={inv} />
          </HStack>
          <HStack spacing='sm' flexShrink={0}>
            <Text fontSize='sm' fontWeight='medium' color='gray.700' whiteSpace='nowrap'>
              {formatAed(inv.totalAmount)}
            </Text>
            <Text fontSize='xs' color='gray.400' whiteSpace='nowrap'>
              {dayjs(inv.createdAt).format('DD MMM')}
            </Text>
            {inv.scope === 'order' && !inv.isPaid && <MarkPaidPopover invoiceId={inv.id} onSaved={invalidate} />}
            <Menu>
              <MenuButton
                as={IconButton}
                aria-label='invoice-actions'
                icon={<Icon as={FiMoreVertical} boxSize='14px' />}
                size='xs'
                variant='ghost'
              />
              <MenuList zIndex='popover'>
                <MenuItem
                  isDisabled={!!regenerateDisabledReason(inv)}
                  title={regenerateDisabledReason(inv) ?? undefined}
                  onClick={() => regenerate({ invoiceId: inv.id })}>
                  Regenerate PDF
                </MenuItem>
                {inv.scope === 'order' && inv.isPaid && (
                  <MenuItem onClick={() => updatePayment({ invoiceId: inv.id, isPaid: false })}>
                    Mark as unpaid
                  </MenuItem>
                )}
              </MenuList>
            </Menu>
          </HStack>
        </HStack>
      ))}
    </Stack>
  );
};
